import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronRight,
  Loader2,
  Search,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { API } from "service/Interceptor";
import { formatPersonName, serverFiles } from "utils/utils";
import toast from "components/Toast/toast";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const PER_PAGE = 12;

const initialsOf = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const PersonAvatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="h-11 w-11 rounded-full object-cover shrink-0 ring-2 ring-zinc-800"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div className="avatar-fallback h-11 w-11 rounded-full shrink-0 flex items-center justify-center font-display font-bold text-sm ring-2 ring-[#6C63F5]/25">
      {initialsOf(name)}
    </div>
  );

const ConnectionRow = ({ person, onNavigate, index }) => {
  const name =
    formatPersonName(person?.firstName, person?.lastName) ||
    person?.companyName ||
    "User";
  const avatar = person?.profileUrl
    ? serverFiles(person.profileUrl)
    : person?.logo
      ? serverFiles(person.logo)
      : null;
  const alias = person?.userAliasId;
  const to = alias ? `/user-profile/${alias}` : undefined;

  const inner = (
    <>
      <PersonAvatar src={avatar} name={name} />
      <div className="min-w-0 flex-1">
        <div className="font-display font-semibold text-sm text-zinc-50 truncate group-hover:text-[#c9c5ff] transition-colors">
          {name}
        </div>
      </div>
      <ChevronRight
        size={16}
        className="text-zinc-600 group-hover:text-[#8b83ff] transition-colors shrink-0"
      />
    </>
  );

  const className =
    "group flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-zinc-900/80 border border-transparent hover:border-zinc-800";

  if (!to) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.24) }}
        className={className}
      >
        {inner}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.24) }}
    >
      <Link
        to={to}
        onClick={onNavigate}
        className={className}
      >
        {inner}
      </Link>
    </motion.div>
  );
};

/**
 * Modern followers / following popup for user profiles.
 * @param {'FOLLOWERS'|'FOLLOWING'} initialType
 */
const FollowConnectionsModal = ({
  open,
  onClose,
  userId,
  initialType = "FOLLOWERS",
  followerCount,
  followingCount,
}) => {
  const [tab, setTab] = useState(
    initialType === "FOLLOWING" ? "FOLLOWING" : "FOLLOWERS"
  );
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followersTotal, setFollowersTotal] = useState(0);
  const [followingTotal, setFollowingTotal] = useState(0);
  const [followersPage, setFollowersPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef(null);
  const fetchLock = useRef(false);

  const reset = useCallback(() => {
    setFollowers([]);
    setFollowing([]);
    setFollowersTotal(Number(followerCount) || 0);
    setFollowingTotal(Number(followingCount) || 0);
    setFollowersPage(1);
    setFollowingPage(1);
    setQuery("");
    setLoading(false);
    setLoadingMore(false);
    fetchLock.current = false;
  }, [followerCount, followingCount]);

  const fetchPage = useCallback(
    async (type, page, { append } = { append: false }) => {
      if (!userId || fetchLock.current) return;
      fetchLock.current = true;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const path =
          type === "FOLLOWERS"
            ? `api/users/${userId}/followers`
            : `api/users/${userId}/followings`;
        const res = await API.get(path, {
          params: { perPage: PER_PAGE, pageNo: page },
        });
        if (!res?.data?.success) {
          toast.error(res?.data?.message || "Could not load connections");
          return;
        }
        const results = res?.data?.data?.results || [];
        const total = res?.data?.data?.totalRecord ?? results.length;
        if (type === "FOLLOWERS") {
          setFollowersTotal(total);
          setFollowers((prev) => (append ? [...prev, ...results] : results));
          setFollowersPage(page);
        } else {
          setFollowingTotal(total);
          setFollowing((prev) => (append ? [...prev, ...results] : results));
          setFollowingPage(page);
        }
      } catch (_) {
        toast.error(
          type === "FOLLOWERS"
            ? "Could not load followers"
            : "Could not load following"
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
        fetchLock.current = false;
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!open) return;
    reset();
    const next = initialType === "FOLLOWING" ? "FOLLOWING" : "FOLLOWERS";
    setTab(next);
    fetchPage(next, 1, { append: false });
  }, [open, initialType, userId, reset, fetchPage]);

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setQuery("");
    const list = next === "FOLLOWERS" ? followers : following;
    if (list.length === 0) {
      fetchPage(next, 1, { append: false });
    }
  };

  const list = tab === "FOLLOWERS" ? followers : following;
  const total = tab === "FOLLOWERS" ? followersTotal : followingTotal;
  const page = tab === "FOLLOWERS" ? followersPage : followingPage;
  const hasMore = list.length < total;

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = formatPersonName(p?.firstName, p?.lastName).toLowerCase();
      const company = String(p?.companyName || "").toLowerCase();
      const role = Array.isArray(p?.industry)
        ? p.industry.join(" ").toLowerCase()
        : String(p?.industry || "").toLowerCase();
      return (
        name.includes(q) || company.includes(q) || role.includes(q)
      );
    });
  })();

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop > el.clientHeight + 64) return;
    if (loading || loadingMore || !hasMore || query.trim()) return;
    fetchPage(tab, page + 1, { append: true });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[75] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 22, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/40 overflow-hidden flex flex-col max-h-[min(88vh,36rem)]"
          >
            <div className="relative px-5 pt-5 pb-4 border-b border-zinc-800/80">
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 60% at 10% -20%, rgba(108,99,245,0.35), transparent 55%)",
                }}
              />
              <div className="relative flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-[#6C63F5]/15 border border-[#6C63F5]/25 flex items-center justify-center shrink-0">
                    <Users size={18} className="text-[#8b83ff]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[0.65rem] uppercase tracking-[0.16em] text-[#8b83ff] font-semibold">
                      Connections
                    </div>
                    <h3 className="font-display font-bold text-xl tracking-tight text-zinc-50 truncate">
                      {tab === "FOLLOWERS" ? "Followers" : "Following"}
                    </h3>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="h-9 w-9 rounded-full inline-flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 transition-colors shrink-0"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="relative mt-4 flex rounded-full bg-zinc-900/80 border border-zinc-800 p-1">
                {[
                  {
                    key: "FOLLOWERS",
                    label: "Followers",
                    count: followersTotal,
                  },
                  {
                    key: "FOLLOWING",
                    label: "Following",
                    count: followingTotal,
                  },
                ].map((t) => {
                  const active = tab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => switchTab(t.key)}
                      className={`relative flex-1 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${
                        active
                          ? "text-white"
                          : "text-zinc-500 hover:text-zinc-300"
                      }`}
                    >
                      {active && (
                        <motion.span
                          layoutId="follow-tab-pill"
                          className="absolute inset-0 rounded-full bg-[#6C63F5]"
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 32,
                          }}
                        />
                      )}
                      <span className="relative inline-flex items-center justify-center gap-1.5 w-full">
                        {t.label}
                        <span
                          className={`tabular-nums ${
                            active ? "text-white/80" : "text-zinc-600"
                          }`}
                        >
                          {t.count}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="relative mt-3">
                <Search
                  size={14}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${tab === "FOLLOWERS" ? "followers" : "following"}…`}
                  className="w-full rounded-full bg-zinc-900/70 border border-zinc-800 pl-9 pr-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[#6C63F5] transition-colors"
                />
              </div>
            </div>

            <div
              ref={listRef}
              onScroll={onScroll}
              className="flex-1 overflow-y-auto px-2 py-2 min-h-[14rem]"
            >
              {loading && list.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center gap-3 text-zinc-500">
                  <Loader2 size={22} className="animate-spin text-[#8b83ff]" />
                  <span className="text-sm">Loading…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-14 px-6 flex flex-col items-center text-center">
                  <div className="h-14 w-14 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4">
                    <UserRound size={24} className="text-zinc-600" />
                  </div>
                  <p className="font-display font-semibold text-zinc-200">
                    {query.trim()
                      ? "No matches"
                      : tab === "FOLLOWERS"
                        ? "No followers yet"
                        : "Not following anyone yet"}
                  </p>
                  <p className="mt-1.5 text-sm text-zinc-500 max-w-[16rem]">
                    {query.trim()
                      ? "Try a different name or role."
                      : tab === "FOLLOWERS"
                        ? "When people follow this profile, they’ll show up here."
                        : "Profiles this user follows will appear here."}
                  </p>
                </div>
              ) : (
                <div className="space-y-0.5 pb-2">
                  {filtered.map((person, i) => (
                    <ConnectionRow
                      key={person?.userAliasId || person?.id || i}
                      person={person}
                      index={i}
                      onNavigate={onClose}
                    />
                  ))}
                  {loadingMore && (
                    <div className="py-4 flex justify-center text-zinc-500">
                      <Loader2
                        size={16}
                        className="animate-spin text-[#8b83ff]"
                      />
                    </div>
                  )}
                  {!hasMore && !query.trim() && list.length > 0 && (
                    <p className="py-3 text-center text-[0.7rem] text-zinc-600">
                      That’s everyone
                    </p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FollowConnectionsModal;
